import { useEffect, useState } from "react";
import BrandMark from "./BrandMark";
import { useI18n } from "../i18n";

const STORAGE_KEY = "ageVerified";

function readStored() {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(value) {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // localStorage can throw in private mode — accept the gate will reappear.
  }
}

export default function AgeVerification({ children }) {
  const { t } = useI18n();
  const [status, setStatus] = useState(() => readStored());

  useEffect(() => {
    if (status === "yes") return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [status]);

  if (status === "yes") {
    return children;
  }

  if (status === "no") {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-amber-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4">
        <div className="max-w-md w-full text-center">
          <div
            className="mx-auto w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 flex items-center justify-center mb-5"
            aria-hidden="true"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="4" y="10" width="16" height="11" rx="2.5" strokeWidth={1.75} />
              <path strokeLinecap="round" strokeWidth={1.75} d="M8 10V7a4 4 0 0 1 8 0v3" />
            </svg>
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-50 mb-3">
            {t("age.blockedTitle")}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
            {t("age.blockedMessage")}
          </p>
        </div>
      </div>
    );
  }

  // The backdrop is tinted toward the brand's brown rather than neutral black:
  // against pure black the warm card read as a harsh cut-out.
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="age-gate-title"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[#2b2621]/80 backdrop-blur-sm p-4"
    >
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 max-w-md w-full p-6 sm:p-8 text-center">
        <BrandMark className="h-14 w-14 mx-auto mb-4" />
        <p className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-2">
          BrewSpot · 18+
        </p>
        <h1
          id="age-gate-title"
          className="font-display text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-50 mb-3"
        >
          {t("age.title")}
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
          {t("age.subtitle")}
        </p>
        <p className="text-base font-medium text-gray-800 dark:text-gray-200 mb-5">
          {t("age.question")}
        </p>
        {/* Confirm is the whole point of the dialog, so it takes the full
            width and "Нет" reads as the quiet way out rather than a second
            equally-weighted choice. */}
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => {
              writeStored("yes");
              setStatus("yes");
            }}
            className="w-full px-4 py-3.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900"
          >
            {t("age.confirm")}
          </button>
          <button
            type="button"
            onClick={() => {
              writeStored("no");
              setStatus("no");
            }}
            className="w-full px-4 py-2 rounded-lg text-sm text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100 font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900"
          >
            {t("age.deny")}
          </button>
        </div>
        <p className="mt-6 text-xs text-gray-500 dark:text-gray-500">{t("age.disclaimer")}</p>
      </div>
    </div>
  );
}
